import React, { Component } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Restaurant as NutritionIcon,
  Assessment as AssessmentIcon,
  FitnessCenter as FitnessIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Favorite as HeartIcon
} from '@mui/icons-material';

// Higher-order component to provide hooks to class component
const withHooks = (Component) => {
  return (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    return <Component {...props} theme={theme} isMobile={isMobile} />;
  };
};

class ServicesSection extends Component {
  render() {
    const { isMobile } = this.props;

    const services = [
      {
        icon: PersonIcon,
        title: 'Personal Training',
        subtitle: '1-on-1 & Small Group Sessions',
        description: 'Get personalized attention with our certified trainers who create custom workout plans tailored to your specific goals and fitness level.',
        features: [
          'Customized workout plans',
          'Proper form instruction',
          'Goal-specific programming',
          'Progress tracking',
          'Flexible scheduling'
        ],
        pricing: 'Choose a Trainer',
        popular: true,
        color: 'primary.main'
      },
      /* {
        icon: GroupIcon,
        title: 'Group Classes',
        subtitle: 'Community-Driven Workouts',
        description: 'Join our energetic group classes that combine effective workouts with the motivation of training alongside others.',
        features: [
          'HIIT classes',
          'Strength circuits',
          'Yoga & mobility',
          'Bootcamp sessions',
          'Beginner-friendly options',
          'All fitness levels welcome'
        ],
        pricing: 'Included with membership',
        popular: false,
        color: 'primary.main'
      }, */
      {
        icon: FitnessIcon,
        title: 'Gym Access',
        subtitle: 'Train on Your Schedule',
        description: 'Access our fully equipped gym with state-of-the-art equipment whenever it fits your schedule.',
        features: [
          'Personal access codes',
          'Modern equipment',
          'Free weights & machines',
          'Cardio equipment',
          'Functional training area',

        ],
        pricing: '$50/month',
        popular: true,
        color: 'secondary.main'
      }
    ];

    const membershipPackages = [
      {
        name: 'Starter',
        price: '$49',
        period: '/month',
        description: 'Perfect for self-motivated individuals',
        features: [
          '24/7 gym access',
          'Basic equipment use',
          'Locker room access',
          'Free fitness assessment'
        ],
        buttonText: 'Get Started',
        popular: false
      },
      {
        name: 'Complete',
        price: '$99',
        period: '/month',
        description: 'Our most popular comprehensive package',
        features: [
          'Everything in Starter',
          'Unlimited group classes',
          'Monthly personal training session',
          'Nutritional consultation',
          'Progress tracking app'
        ],
        buttonText: 'Most Popular',
        popular: true
      },
      {
        name: 'Elite',
        price: '$199',
        period: '/month',
        description: 'Premium experience with maximum support',
        features: [
          'Everything in Complete',
          '4 personal training sessions',
          'Custom meal planning',
          'Priority class booking',
          'Guest passes (2/month)',
          'Supplement discounts'
        ],
        buttonText: 'Go Elite',
        popular: false
      }
    ];

    return (
      
      <Box sx={{ backgroundColor: 'white', pb: 8 }}>
        <div className="about-hero">
        <div className="hero-content">
          <h1>
            Our<br />
            Services
          </h1>
          <p>Comprehensive fitness solutions designed to meet you wherever you are in your journey</p>
        </div>
      </div>
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}></Box>
          {/* Services Grid */}
          <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                color: 'text.primary'
              }}
            >
              Services 
          </Typography>
          <Box sx={{ textAlign: 'center', mb: 8 }}></Box>
          <Grid container spacing={4} sx={{ mb: 10, justifyContent: 'center' }}>
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Grid item xs={12} md={6} lg={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Paper
                    className="hover-lift"
                    sx={{
                      height: '100%',
                      maxWidth: 400,
                      width: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      borderColor: 'grey.200',
                      p: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      mx: 'auto'
                    }}
                  >

                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Service Icon */}
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: service.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          mx: 'auto'
                        }}
                      >
                        <IconComponent sx={{ fontSize: 40, color: 'white' }} />
                      </Box>

                      {/* Service Title */}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          textAlign: 'center',
                          color: 'text.primary'
                        }}
                      >
                        {service.title}
                      </Typography>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: service.color,
                          fontWeight: 500,
                          mb: 2,
                          textAlign: 'center'
                        }}
                      >
                        {service.subtitle}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          mb: 3,
                          textAlign: 'center',
                          flexGrow: 1
                        }}
                      >
                        {service.description}
                      </Typography>

                      {/* Features List */}
                      <List dense sx={{ mb: 3 }}>
                        {service.features.map((feature, featureIndex) => (
                          <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckIcon sx={{ fontSize: 20, color: service.color }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              sx={{
                                '& .MuiTypography-root': {
                                  fontSize: '0.9rem',
                                  color: 'text.secondary'
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>


                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    );
  }
}

export default withHooks(ServicesSection);
